import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
// @mui
import { Box, Button, Divider, Stack, Typography, Alert } from '@mui/material';
import { LoadingButton } from '@mui/lab';
// hooks / config
import useAuth from '../../hooks/useAuth';
import { PATH_AFTER_LOGIN } from '../../config';

const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || '';
const APPLE_CLIENT_ID = process.env.REACT_APP_APPLE_CLIENT_ID || '';
const APPLE_REDIRECT =
  process.env.REACT_APP_APPLE_REDIRECT ||
  (typeof window !== 'undefined' ? window.location.origin : '');

function loadScript(src, id) {
  return new Promise((resolve, reject) => {
    if (document.getElementById(id)) {
      resolve();
      return;
    }
    const el = document.createElement('script');
    el.src = src;
    el.id = id;
    el.async = true;
    el.defer = true;
    el.onload = () => resolve();
    el.onerror = () => reject(new Error(`failed to load ${src}`));
    document.body.appendChild(el);
  });
}

// Official multi-colour Google "G"
function GoogleG() {
  return (
    <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden>
      <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.9 29.3 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34.5 6.1 29.5 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.3-.4-3.5z" />
      <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 16 19 12 24 12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34.5 6.1 29.5 4 24 4 16.3 4 9.7 8.3 6.3 14.7z" />
      <path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.3 0-9.7-3.1-11.3-7.6l-6.5 5C9.6 39.6 16.2 44 24 44z" />
      <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.1-4.1 5.6l6.2 5.2C41.4 36 44 30.5 44 24c0-1.3-.1-2.3-.4-3.5z" />
    </svg>
  );
}

function AppleLogo() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="#fff" aria-hidden>
      <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.53 4.09zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" />
    </svg>
  );
}

const socialBtnSx = {
  height: 50,
  borderRadius: 2,
  fontWeight: 700,
  fontSize: '0.94rem',
  textTransform: 'none',
  justifyContent: 'center',
  gap: 1.2,
};

export default function SocialAuth() {
  const { loginWithGoogle, loginWithApple } = useAuth();
  const navigate = useNavigate();
  const [error, setError] = useState('');
  const [busy, setBusy] = useState('');
  const googleInited = useRef(false);

  const finish = () => navigate(PATH_AFTER_LOGIN, { replace: true });
  const showErr = (e) =>
    setError(e?.response?.data?.message || e?.message || 'Sign-in failed. Please try again.');

  // Initialise Google Identity Services (One Tap / popup credential)
  useEffect(() => {
    if (!GOOGLE_CLIENT_ID) return;
    loadScript('https://accounts.google.com/gsi/client', 'gsi-script')
      .then(() => {
        if (!window.google || googleInited.current) return;
        googleInited.current = true;
        window.google.accounts.id.initialize({
          client_id: GOOGLE_CLIENT_ID,
          callback: async (resp) => {
            try {
              setBusy('google');
              setError('');
              await loginWithGoogle(resp.credential);
              finish();
            } catch (e) {
              showErr(e);
            } finally {
              setBusy('');
            }
          },
        });
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleGoogle = () => {
    setError('');
    if (!GOOGLE_CLIENT_ID || !window.google || !googleInited.current) {
      setError('Google Sign-In isn’t configured yet. Add REACT_APP_GOOGLE_CLIENT_ID.');
      return;
    }
    window.google.accounts.id.prompt();
  };

  const handleApple = async () => {
    setError('');
    if (!APPLE_CLIENT_ID) {
      setError('Apple Sign-In isn’t configured yet. Add REACT_APP_APPLE_CLIENT_ID.');
      return;
    }
    try {
      setBusy('apple');
      await loadScript(
        'https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js',
        'apple-script'
      );
      window.AppleID.auth.init({
        clientId: APPLE_CLIENT_ID,
        scope: 'name email',
        redirectURI: APPLE_REDIRECT,
        usePopup: true,
      });
      const res = await window.AppleID.auth.signIn();
      const idToken = res?.authorization?.id_token;
      const email = res?.user?.email;
      await loginWithApple(idToken, email);
      finish();
    } catch (e) {
      if (e?.error === 'popup_closed_by_user') return;
      showErr(e);
    } finally {
      setBusy('');
    }
  };

  return (
    <Stack spacing={2}>
      <Divider sx={{ my: 0.5, '& .MuiDivider-wrapper': { px: 1.5 } }}>
        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.45)', fontWeight: 600 }}>
          or continue with
        </Typography>
      </Divider>

      {error && <Alert severity="error">{error}</Alert>}

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
        <LoadingButton
          fullWidth
          loading={busy === 'google'}
          onClick={handleGoogle}
          startIcon={<GoogleG />}
          sx={{
            ...socialBtnSx,
            bgcolor: '#fff',
            color: '#111',
            '&:hover': { bgcolor: '#f1f1ef' },
          }}
        >
          Google
        </LoadingButton>

        <LoadingButton
          fullWidth
          loading={busy === 'apple'}
          onClick={handleApple}
          startIcon={<AppleLogo />}
          sx={{
            ...socialBtnSx,
            bgcolor: '#000',
            color: '#fff',
            border: '1px solid rgba(255,255,255,0.18)',
            '&:hover': { bgcolor: '#1a1a1a' },
          }}
        >
          Apple
        </LoadingButton>
      </Stack>
    </Stack>
  );
}
